import React, { lazy, Suspense, useState, useEffect } from "react";
import Preloader from "../src/components/Pre";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";

import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate
} from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import SeoManager from "./components/SeoManager";
import "./style.css";
import "./App.css";
import "bootstrap/dist/css/bootstrap.min.css";
const lazyNamed = (loader, exportName) =>
  lazy(() => loader().then((module) => ({ default: module[exportName] })));

const loadCoreRoutes = () => import("./routeChunks/core");
const loadAboutRoutes = () => import("./routeChunks/about");
const loadCareerRoutes = () => import("./routeChunks/career");
const loadServicesRoutes = () => import("./routeChunks/services");
const loadProgramsRoutes = () => import("./routeChunks/programs");
const loadPoliciesRoutes = () => import("./routeChunks/policies");

const Home = lazyNamed(loadCoreRoutes, "Home");
const Contact = lazyNamed(loadCoreRoutes, "Contact");
const Team = lazyNamed(loadCoreRoutes, "Team");
const Registration = lazyNamed(loadCoreRoutes, "Registration");

const About = lazyNamed(loadAboutRoutes, "About");
const Story = lazyNamed(loadAboutRoutes, "Story");
const Mission = lazyNamed(loadAboutRoutes, "Mission");
const Partners = lazyNamed(loadAboutRoutes, "Partners");
const Testimonial = lazyNamed(loadAboutRoutes, "Testimonial");

const Projects = lazyNamed(loadCareerRoutes, "Projects");
const AdmissionsPage = lazyNamed(loadCareerRoutes, "AdmissionsPage");
const CatalogPage = lazyNamed(loadCareerRoutes, "CatalogPage");
const StudentResourcesPage = lazyNamed(loadCareerRoutes, "StudentResourcesPage");
const Tiers = lazyNamed(loadCareerRoutes, "Tiers");
const TrainingsTiers = lazyNamed(loadCareerRoutes, "TrainingsTiers");

const Favrhood = lazyNamed(loadServicesRoutes, "Favrhood");
const ConsultingAdvisory = lazyNamed(loadServicesRoutes, "ConsultingAdvisory");
const Lab = lazyNamed(loadServicesRoutes, "Lab");
const WebPricing = lazyNamed(loadServicesRoutes, "WebPricing");
const RoboticsPricing = lazyNamed(loadServicesRoutes, "RoboticsPricing");
const ThreeDWebPricing = lazyNamed(loadServicesRoutes, "ThreeDWebPricing");
const ThreeDModelingPricing = lazyNamed(loadServicesRoutes, "ThreeDModelingPricing");
const JusticeTechPricing = lazyNamed(loadServicesRoutes, "JusticeTechPricing");
const AppropTechPricing = lazyNamed(loadServicesRoutes, "AppropTechPricing");

const Stats = lazyNamed(loadProgramsRoutes, "Stats");
const ImpactHome = lazyNamed(loadProgramsRoutes, "ImpactHome");
const Impact = lazyNamed(loadProgramsRoutes, "Impact");
const HandsOnLearning = lazyNamed(loadProgramsRoutes, "HandsOnLearning");
const ExploratoryLearning = lazyNamed(loadProgramsRoutes, "ExploratoryLearning");
const Workshop = lazyNamed(loadProgramsRoutes, "Workshop");
const Afterschool = lazyNamed(loadProgramsRoutes, "Afterschool");
const Ambassadors = lazyNamed(loadProgramsRoutes, "Ambassadors");
const TechPal = lazyNamed(loadProgramsRoutes, "TechPal");
const Camps = lazyNamed(loadProgramsRoutes, "Camps");
const Senior = lazyNamed(loadProgramsRoutes, "Senior");
const Neighborhood = lazyNamed(loadProgramsRoutes, "Neighborhood");
const PartnerWithUs = lazyNamed(loadProgramsRoutes, "PartnerWithUs");
const VolunteerOpportunities = lazyNamed(loadProgramsRoutes, "VolunteerOpportunities");
const InternExperience = lazyNamed(loadProgramsRoutes, "InternExperience");
const Support = lazyNamed(loadProgramsRoutes, "Support");

const GrievanceAppealsPolicy = lazyNamed(loadPoliciesRoutes, "GrievanceAppealsPolicy");
const PrivacyPolicy = lazyNamed(loadPoliciesRoutes, "PrivacyPolicy");
const TermsOfUse = lazyNamed(loadPoliciesRoutes, "TermsOfUse");



function App() {
  const [load, upadateLoad] = useState(true);
  const [theme, setTheme] = useState(() => {
    try {
      const saved = window.localStorage.getItem("cf-theme");
      return saved === "light" || saved === "dark" ? saved : "light";
    } catch {
      return "light";
    }
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      upadateLoad(false);
    }, 250);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    try {
      document.documentElement.setAttribute("data-theme", theme);
      window.localStorage.setItem("cf-theme", theme);
    } catch {
      document.documentElement.setAttribute("data-theme", theme);
    }

    const metaTheme = document.querySelector('meta[name="theme-color"]');
    if (metaTheme) {
      metaTheme.setAttribute("content", theme === "light" ? "#0b6f7d" : "#06161d");
    }
  }, [theme]);

  useEffect(() => {
    let rafId = null;
    let scheduled = false;

    const optimizeImages = () => {
      scheduled = false;
      const viewportHeight = window.innerHeight || 800;
      const images = document.querySelectorAll("img");

      images.forEach((img, idx) => {
        if (!img.hasAttribute("decoding")) {
          img.setAttribute("decoding", "async");
        }

        const rect = img.getBoundingClientRect();
        const nearViewport = rect.top < viewportHeight * 1.25 && rect.bottom > -viewportHeight * 0.25;

        if (!img.hasAttribute("loading")) {
          const shouldEagerLoad = nearViewport && idx < 2;
          img.setAttribute("loading", shouldEagerLoad ? "eager" : "lazy");
        }

        if (!img.hasAttribute("fetchpriority")) {
          const isHeroCandidate = nearViewport && rect.top < viewportHeight * 0.9;
          if (isHeroCandidate && img.getAttribute("loading") === "eager") {
            img.setAttribute("fetchpriority", "high");
          } else if (img.getAttribute("loading") === "lazy") {
            img.setAttribute("fetchpriority", "low");
          }
        }
      });
    };

    const scheduleOptimization = () => {
      if (scheduled) return;
      scheduled = true;
      rafId = window.requestAnimationFrame(optimizeImages);
    };

    scheduleOptimization();

    const observer = new MutationObserver(() => {
      scheduleOptimization();
    });

    if (document.body) {
      observer.observe(document.body, { childList: true, subtree: true });
    }

    window.addEventListener("resize", scheduleOptimization, { passive: true });
    window.addEventListener("load", scheduleOptimization, { once: true });

    return () => {
      observer.disconnect();
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
      window.removeEventListener("resize", scheduleOptimization);
      window.removeEventListener("load", scheduleOptimization);
    };
  }, []);

  const toggleTheme = () => {
    setTheme((current) => (current === "dark" ? "light" : "dark"));
  };

  return (
    <Router>
      <SeoManager />
      <Preloader load={load} />
      <div className="App" id={load ? "no-scroll" : "scroll"}>
        <Navbar theme={theme} onToggleTheme={toggleTheme} />
        <ScrollToTop />
        <Suspense fallback={<div className="project-section" aria-live="polite" />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/courses" element={<Projects />} />
            <Route path="/admissions" element={<AdmissionsPage />} />
            <Route path="/catalog" element={<CatalogPage />} />
            <Route path="/student-resources" element={<StudentResourcesPage />} />
            <Route path="/tiers" element={<Tiers />} />
            <Route path="/programs/trainings-tiers" element={<TrainingsTiers />} />
            <Route path="/about/who-we-are" element={<About />} />
            <Route path="/registration" element={<Registration />} />
            <Route path="/impact" element={<Stats />} />
            <Route path="/impact/community" element={<ImpactHome />} />
            <Route path="/team" element={<Team />} />
            <Route path="/about/testimonials" element={<Testimonial />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/web-pricing" element={<WebPricing />} />
            <Route path="/robotics-pricing" element={<RoboticsPricing />} />
            <Route path="/three-d-web-pricing" element={<ThreeDWebPricing />} />
            <Route path="/three-d-modeling-pricing" element={<ThreeDModelingPricing />} />
            <Route path="/justice-tech-pricing" element={<JusticeTechPricing />} />
            <Route path="/approp-tech-pricing" element={<AppropTechPricing />} />
            <Route path="/services/favrhood" element={<Favrhood />} />
            <Route path="/services/consulting-advisory" element={<ConsultingAdvisory />} />
            <Route path="/favrhood" element={<Navigate to="/services/favrhood" replace />} />
            <Route path="/lab" element={<Lab />} />
            <Route path="/programs/syep-internship" element={<Impact />} />
            <Route path="/programs/hands-on-learning" element={<HandsOnLearning />} />
            <Route path="/programs/exploratory-learning" element={<ExploratoryLearning />} />
            <Route path="/programs/workshops" element={<Workshop />} />
            <Route path="/programs/after-school" element={<Afterschool />} />
            <Route path="/programs/ambassadors" element={<Ambassadors />} />
            <Route path="/programs/tech-pal" element={<TechPal />} />
            <Route path="/programs/partner-with-us" element={<PartnerWithUs />} />
            <Route path="/programs/volunteer-opportunities" element={<VolunteerOpportunities />} />
            <Route path="/programs/tech-camps" element={<Camps />} />
            <Route path="/programs/senior-citizens" element={<Senior />} />
            <Route path="/programs/code-and-soil" element={<Neighborhood />} />
            <Route path="/support" element={<Support />} />
            <Route path="/about/our-story" element={<Story />} />
            <Route path="/about/our-partners" element={<Partners />} />
            <Route path="/about/our-mission" element={<Mission />} />
            <Route path="/programs/intern-experience" element={<InternExperience />} />
            <Route path="/about/impact" element={<Navigate to="/impact" replace />} />
            <Route path="/policies/grievance-appeals" element={<GrievanceAppealsPolicy />} />
            <Route path="/policies/privacy" element={<PrivacyPolicy />} />
            <Route path="/policies/terms" element={<TermsOfUse />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </Suspense>
        <Footer />
      </div>
    </Router>
  );
}

export default App;
